import React, { useState, useEffect } from 'react'
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material'
import {
  ArrowBack,
  CloudUpload,
  List as ListIcon,
  Home,
  Folder,
  Description
} from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import DocumentUpload from '../components/mrd/Documents/DocumentUpload'
import DocumentList from '../components/mrd/Documents/DocumentList'
import mrdService from '../services/mrdservice'

export const DocumentsPage = () => {
  const { fileId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [tabValue, setTabValue] = useState(0)
  const [mrdFile, setMrdFile] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (fileId) {
      loadMRDFile()
    } else {
      // Redirect to MRD Files if no fileId provided
      navigate('/hospital/mrd/mrd-files')
    }
  }, [fileId, navigate])

  const loadMRDFile = async () => {
    try {
      const response = await mrdService.getFileById(fileId)
      if (response.data.success) {
        setMrdFile(response.data.data)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load MRD file details', { variant: 'error' })
    }
  }

  const handleUploadComplete = () => {
    setRefreshKey(prev => prev + 1)
    setTabValue(1) // Switch to documents list
  }

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1)
  }

  // Only render if fileId is provided
  if (!fileId) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          No file ID provided. Redirecting to MRD Files...
        </Alert>
      </Container>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/hospital/mrd/dashboard')}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Dashboard
          </Link>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/hospital/mrd/mrd-files')}
          >
            <Folder sx={{ mr: 0.5 }} fontSize="small" />
            MRD Files
          </Link>
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            color="text.primary"
          >
            <Description sx={{ mr: 0.5 }} fontSize="small" />
            Documents
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Document Management
              </Typography>
              {mrdFile && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                  <Chip
                    label={`File: ${mrdFile.fileNumber}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="body1" color="text.secondary">
                    Patient: <strong>{mrdFile.patientName}</strong>
                  </Typography>
                  {mrdFile.department && (
                    <Chip
                      label={mrdFile.department.name}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/hospital/mrd/mrd-files')}
              size="large"
            >
              Back to Files
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(e, val) => setTabValue(val)}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                minHeight: 72,
                fontSize: '1rem',
                fontWeight: 600
              }
            }}
          >
            <Tab
              label="Upload Documents"
              icon={<CloudUpload />}
              iconPosition="start"
            />
            <Tab
              label="Document List"
              icon={<ListIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {tabValue === 0 && (
          <Paper sx={{ p: 4, borderRadius: 2 }}>
            <DocumentUpload
              fileId={fileId}
              onUploadComplete={handleUploadComplete}
            />
          </Paper>
        )}

        {tabValue === 1 && (
          <Paper sx={{ p: 4, borderRadius: 2 }}>
            <DocumentList
              key={refreshKey}
              fileId={fileId}
              onRefresh={handleRefresh}
            />
          </Paper>
        )}
      </Container>
    </Box>

  )

}


